import { useMemo } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import {
  collectSellerIdsUnder,
  computeTreeConnectors,
  type SellerTreeOption,
} from '@/lib/mise/sellerTree';

interface SellerTreeCheckboxListProps {
  options: SellerTreeOption[];
  selectedIds: Set<string>;
  onToggleSeller: (sellerId: string) => void;
  /** グループ配下の店舗をまとめて選択（selected=true）／解除する */
  onToggleSellers: (sellerIds: string[], selected: boolean) => void;
}

/**
 * 店舗を店舗グループ階層の下に並べたチェックボックス一覧。
 * 同名の店舗（例: 各ブランドの東京店）がどのグループのものか見分けられるようにする。
 * 見た目は「集計前の店舗グループを編集」のツリーに揃えている。
 */
export function SellerTreeCheckboxList({
  options,
  selectedIds,
  onToggleSeller,
  onToggleSellers,
}: SellerTreeCheckboxListProps) {
  const connectors = useMemo(
    () => computeTreeConnectors(options.map((option) => option.depth)),
    [options],
  );

  return (
    <Box
      sx={{
        maxHeight: 400,
        overflow: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      {options.map((option, index) => {
        const { isLast, ancestorIsLast } = connectors[index];
        const isGroup = option.kind === 'group';
        const sellerIds = isGroup ? collectSellerIdsUnder(options, index) : [];
        const selectedCount = sellerIds.filter((id) => selectedIds.has(id)).length;
        const allSelected = sellerIds.length > 0 && selectedCount === sellerIds.length;
        const label = isGroup ? option.label : option.seller.name || option.seller.id;
        const toggle = isGroup
          ? () => onToggleSellers(sellerIds, !allSelected)
          : () => onToggleSeller(option.seller.id);

        return (
          <Box
            key={isGroup ? `group-${option.id}-${index}` : option.seller.id}
            onClick={toggle}
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.5,
              px: 1,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            {/* コネクタ: proportional フォントでズレないよう等幅 span で描画 */}
            <Box
              component="span"
              sx={{
                fontFamily: 'monospace',
                whiteSpace: 'pre',
                userSelect: 'none',
                color: 'text.secondary',
              }}
            >
              {ancestorIsLast.map((isLastAncestor, i) => (
                <span key={i}>{isLastAncestor ? '     ' : '│    '}</span>
              ))}
              {option.depth > 0 && <span>{isLast ? '└─' : '├─'}</span>}
            </Box>
            <Checkbox
              checked={isGroup ? allSelected : selectedIds.has(option.seller.id)}
              indeterminate={isGroup && selectedCount > 0 && !allSelected}
              onChange={toggle}
              onClick={(e) => e.stopPropagation()}
              size="small"
              inputProps={{ 'aria-label': isGroup ? `${label}の店舗をまとめて選択` : label }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: isGroup ? 600 : undefined }}>
                {label}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {isGroup
                  ? `${selectedCount} / ${sellerIds.length}店舗を選択中`
                  : `ID: ${option.seller.id}`}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
